/**
 * WHAT'S NEW, BY ROLE (DECISIONS 120, Bobbie, 2026-10-07).
 *
 * She was worried that "there are new features that the theachers do not
 * know about" and asked "can we create a whats new card based on role?", then
 * "lets remind staff about the shaoutout wall!", picking "Just the What's new
 * card" (no repeating notification). The card on each home shows a person,
 * once, the entries below for THEIR role that she has not cleared, and one
 * tap (Got it) clears everything it showed, on every device and sign-in.
 *
 * NOTHING HERE IS INVENTED. An entry describes only what is live for the roles
 * it lists, worded from the How-To guide entry for the same feature
 * (src/lib/howToGuide.ts). The card starts with the one entry Bobbie asked for
 * by name; later user-visible changes add their own entry here.
 *
 * JR STAFF COPY (Bobbie, 2026-10-07: "JR staff i think the one person will
 * confuse them so leave that off", clarified "Just don't mention it"). Any
 * entry an 'ensemble' viewer can read never mentions her two ways of signing
 * in, aliases, or being one person. tests/whats-new.test.mjs holds that.
 *
 * WHO STARTS WHERE. A new account must not open on months of backlog, so a
 * person sees only entries dated on or after the day her account was created
 * (the earliest of a Jr Staff's accounts, so both of her homes agree), and no
 * entry older than WHATS_NEW_MAX_AGE_DAYS is shown to anyone. The start day is
 * written once, by the server (netlify/functions/whatsNewSeen.mjs).
 *
 * Pure: no Firebase import, so the server lane and node tests import it.
 */
import type { UserRole } from './userRole.ts';

export interface WhatsNewEntry {
  /** Stable forever: the seen state stores it. */
  id: string;
  /** The studio day it went live, YYYY-MM-DD. Never in the future. */
  date: string;
  roles: readonly UserRole[];
  title: string;
  /** One or two plain sentences. */
  body: string;
  /** An in-app route or a /how-to?entry= link. */
  link?: { label: string; href: string };
  /** Shown first and in its own colour. */
  featured?: boolean;
}

/**
 * Nobody is shown an entry older than this, and cleared ids older than this
 * are forgotten. 62 days keeps every day of last month in reach (and
 * remembered) on the last day of a 31-day month after a 31-day month, which
 * the monthly roll-up needs.
 */
export const WHATS_NEW_MAX_AGE_DAYS = 62;

/** The card shows this many before "See what changed"; past the threshold the list gets a search box. */
export const WHATS_NEW_COLLAPSED_COUNT = 3;
export const WHATS_NEW_SEARCH_THRESHOLD = 6;

export const SHOUTOUT_WALL_ENTRY_ID = 'shoutout-wall-here-for-you';

/** Newest first. Add new entries at the top. */
export const WHATS_NEW_ENTRIES: readonly WhatsNewEntry[] = [
  {
    // A database that stopped without a word (2026-11-01: Bobbie's
    // Understood/complete sat on Saving... until she closed the app and opened
    // it again). The Understood/complete and Done with this buttons in
    // Messages, on the class page and on the boards now say Still saving...
    // and run the check (lib/useSavingText.ts); a Dismiss beside them shares
    // their busy state and starts the check, but keeps its own word. Worded
    // from the How-To entry weak-signal-loading, which every role reads. It
    // never mentions how a Jr Staff signs in (DECISIONS 120).
    id: 'still-saving-check',
    date: '2026-11-02',
    roles: ['family', 'director', 'manager', 'company', 'ensemble', 'foh'],
    title: 'A save stuck on Saving... now says what is happening',
    body: 'If Understood/complete or Done with this in Messages, on a class page or on a board is still saving after about eight seconds, it says Still saving... and the Hub checks whether its connection has stopped. If it has, the Hub reloads once or shows The Hub lost its connection. Tap to reload, and the button says Not saved yet, so tap it again afterwards if it is still there. A slow or missing signal never makes the Hub reload. If it still says Still saving... after about a minute and your signal is good, close the Hub fully and open it again, then tap the button again if it is still there.',
    link: { label: 'How it works', href: '/how-to?entry=weak-signal-loading' },
  },
  {
    // Technique skills are one answer per dancer (Bobbie, 2026-11-02: "if
    // one teacher marks it off as having a split ... it should show them as
    // having their split"), the reports read every mark, and a By skill view
    // is new. Worded from the How-To entries roster-pass-teacher-check-off
    // and technique-command-center-reports. Jr Staff copy never mentions By
    // student or By skill, which are not on their page (and never how they
    // sign in, DECISIONS 120).
    id: 'technique-skills-per-dancer',
    date: '2026-11-01',
    roles: ['director', 'manager', 'company'],
    title: 'A skill checked off once counts in every class and report',
    body: 'When a dancer has a skill from one class or from Teacher mastery, every other class working on it shows Has it, and the Technique reports count it. Reports also have a new By skill view, every dancer on a skill tap-to-open in By class, and New Yes by month.',
    link: { label: 'How it works', href: '/how-to?entry=technique-command-center-reports' },
  },
  {
    id: 'technique-skills-per-dancer-jr',
    date: '2026-11-01',
    roles: ['ensemble'],
    title: 'A skill checked off once counts in every class',
    body: 'When a dancer already has a skill from another class or from Teacher mastery, your class checklist shows Has it and counts them as done. You can still mark them for your class.',
    link: { label: 'How it works', href: '/how-to?entry=roster-pass-teacher-check-off' },
  },
  {
    // Menu pins her Menu no longer shows come off at Edit pins (DECISIONS 161;
    // Bobbie, 2026-10-31, her pick "Auto-clear them (Recommended)": "When
    // someone opens Edit pins, any pin for an item their Menu no longer shows
    // is quietly removed from their account, so it stops counting toward
    // 12."). Worded from the How-To entry pin-menu-items. The roles are
    // exactly the people who see Edit pins: every grown-up role with a proven
    // role on the grown-up Menu (NavDrawer.tsx pinsEnabled). A kid's dancer
    // home reads no What's new role, so the kids' bar's half is in the How-To
    // entry dancer-menu-and-search. It never mentions how a Jr Staff signs in
    // (DECISIONS 120).
    id: 'pin-menu-items-auto-clear',
    date: '2026-11-01',
    roles: ['family', 'director', 'manager', 'company', 'ensemble', 'foh'],
    title: 'Pins for rows your Menu no longer shows come off',
    body: 'When you tap Edit pins, a pin for a row your Menu no longer shows is taken off quietly, so it stops counting toward your 12. A row that is still loading, or that another menu of yours still shows, keeps its pin.',
    link: { label: 'How it works', href: '/how-to?entry=pin-menu-items' },
  },
  {
    // Menu pins (DECISIONS 161; Bobbie, 2026-10-24 and 2026-10-26: under
    // Search and Home, saved to her account, up to 12). Worded from the How-To
    // entry pin-menu-items, which every role reads, and every role has a Menu.
    // It never mentions how a Jr Staff signs in (DECISIONS 120).
    id: 'pin-menu-items',
    date: '2026-10-31',
    roles: ['family', 'director', 'manager', 'company', 'ensemble', 'foh'],
    title: 'Pin the menu items you use most',
    body: 'Open the Menu and tap Edit pins. Tap a row to pin it, and it moves up into Pinned, just under Search and Home. You can keep up to 12, and they are saved to your account, so they show up on every phone and computer you use.',
    link: { label: 'How it works', href: '/how-to?entry=pin-menu-items' },
  },
  {
    // The closeout small items (2026-10-29): one pass over the whole Teacher
    // filter list (offeringTeachers.ts teacherFilterOptionsFromRows) gives two
    // different teachers whose options read alike a last initial. Worded from
    // the How-To entries class-teaching-team and
    // technique-command-center-editor. The roles that open Classes & dances
    // (Offerings.tsx canViewClasses, isDirectorFohOrManager; Admin has a
    // Classes tile on her home). The link is the Technique entry, which all
    // three read and which describes both filters.
    id: 'teacher-filter-same-first-name-initial',
    date: '2026-10-29',
    roles: ['director', 'manager', 'foh'],
    title: 'Two teachers who share a first name read apart in the teacher filters',
    body: 'In the Teacher filter on Classes & dances and the teacher filter on the Technique command center, two different teachers who share a first name now get a last initial, like Ms. Ella F. and Ms. Ella G., when the Hub has their last names. Two whose last names start with the same letter can still read alike.',
    link: { label: 'How it works', href: '/how-to?entry=technique-command-center-editor' },
  },
  {
    // The same change for the roles that open the Technique command center
    // but not Classes & dances (techniqueCommandCenterAccess.ts: Executive
    // Teachers and Jr Staff). Admin opens Technique only with the editor
    // switch, and she already has the entry above.
    id: 'technique-teacher-filter-same-first-name-initial',
    date: '2026-10-29',
    roles: ['company', 'ensemble'],
    title: 'Two teachers who share a first name read apart in the Technique teacher filter',
    body: 'On the Technique command center, two different teachers who share a first name now get a last initial in the teacher filter, like Ms. Ella F. and Ms. Ella G., when the Hub has their last names. Two whose last names start with the same letter can still read alike.',
    link: { label: 'How it works', href: '/how-to?entry=technique-command-center-editor' },
  },
  {
    // The closeout polish (2026-10-27): the Technique command center's
    // teacher filter is one option per person, the way the Teacher filter on
    // Classes & dances is since J11, and the staff calendar reads Someone for
    // an assigned teacher with no saved name. Worded from the How-To entries
    // technique-command-center-editor and where-jr-assistants-show. Directors
    // and Managers: the two roles that use both the Class skills filters and
    // the calendar every week. The link opens an entry both roles read.
    id: 'technique-teacher-filter-once-calendar-someone',
    date: '2026-10-27',
    roles: ['director', 'manager'],
    title: 'The Technique teacher filter lists each teacher once',
    body: 'On the Technique command center, the teacher filter now lists each teacher once whenever the Hub can confirm who is who, and choosing a teacher shows every class they teach. On the calendar, a teacher who is assigned but has no saved name reads Someone instead of disappearing.',
    link: { label: 'How it works', href: '/how-to?entry=technique-command-center-editor' },
  },
  {
    // PR #1466 (Bobbie, 2026-10-26: "Can you make what your voting for
    // larger?"): each nomination card on My Nominations is headed by the
    // award's name in large type, with the month under it
    // (studentAwards/AwardCardHeading.tsx). Worded from the How-To entries
    // nominate-monthly-award and vote-jr-staff-mvp. The roles are the ones
    // that nominate or vote (nominate-monthly-award's roles).
    id: 'award-card-heading-larger',
    date: '2026-10-26',
    roles: ['director', 'manager', 'company', 'ensemble'],
    title: 'The award you are picking for is the big heading now',
    body: "On My Nominations in Student Awards, each nomination card is headed by the award's name in large type, with the month under it, so you can see at a glance which award you are picking for.",
    link: { label: 'How it works', href: '/how-to?entry=nominate-monthly-award' },
  },
  {
    // The 10/12 reconcile, item 6: the Recital/Comp Tracker card on To-dos
    // now lists a Jr Staff's classes, and saves a missing song name, the same
    // way however she signs in. Worded from the How-To entry make-a-task.
    // Jr Staff only, and it never says how she signs in (DECISIONS 120).
    id: 'jr-staff-recital-comp-card-song-name',
    date: '2026-10-26',
    roles: ['ensemble'],
    title: 'Your classes on the Recital/Comp Tracker card',
    body: 'The Recital/Comp Tracker card on To-dos lists the classes you teach that still need recital or comp work. Missing a song name? Type it right on the card and tap Save.',
    link: { label: 'Open To-dos', href: '/tasks' },
  },
  {
    // Bobbie, 2026-10-04 (DECISIONS 100): "Yes, make them filters". Worded
    // from the How-To entry sub-board-compact-requests. The roles are the
    // ones that see the tiles: every staff role that holds a sub board. The
    // Admin desk keeps its own tonight view, with no tiles.
    id: 'sub-board-tile-filters',
    date: '2026-10-26',
    roles: ['director', 'manager', 'company', 'ensemble'],
    title: 'Sub Board tiles are filters now',
    body: "Tap Uncovered, Covered or You're on at the top of the Sub Board to see just those requests. Tap it again, or Show whole board, to see everything.",
    link: { label: 'Open the Sub Board', href: '/subs' },
  },
  {
    // J11 (DECISIONS 26; Bobbie on the Classes card, "doubles are still
    // htere"). Worded from the How-To entries class-teaching-team,
    // production-report-final-list and the songs and rosters sheet. For the
    // three roles that open all three screens: Classes & dances and the
    // tracker export are isDirectorFohOrManager, and the Final List
    // (canViewProductionReport) takes those three plus 'company'. Not for
    // 'ensemble': Jr Staff copy never mentions her two accounts (header
    // above). Not for 'company': an Executive Teacher opens the Final List
    // but not the Classes & dances card or the sheet this names first. The
    // link opens the songs and rosters sheet entry, whose audience is exactly
    // these three roles; class-teaching-team is for Directors and Managers
    // only, so an Admin following it would land on an entry she cannot read.
    id: 'teacher-names-once-and-someone',
    date: '2026-10-26',
    roles: ['director', 'manager', 'foh'],
    title: 'Each teacher shows once on a class',
    body: 'A Jr Staff who teaches a class on both of her linked accounts is now named once on the Classes & dances card, the songs and rosters sheet and the Final List, whenever the Hub can confirm the link. A teacher who is assigned but has no saved name reads Someone instead of disappearing.',
    link: { label: 'How it works', href: '/how-to?entry=tracker-songs-and-rosters-sheet' },
  },
  {
    // Three things a dancer would notice on her own screens, from the
    // 2026-10-10 unfinished-business list: schedule cards name who teaches,
    // My profile dances open their class, and her photo wears her color. The
    // dancer sign-in has no card of its own, so it goes to the grown-ups who
    // set it up. Every fact here is the How-To entries dancer-calendar-dates
    // and dancer-home-tour. Not 'ensemble': her staff home has none of these
    // screens, and the card there is the same card.
    id: 'dancer-schedule-names-profile-classes',
    date: '2026-10-26',
    roles: ['family'],
    title: 'Your dancer\'s schedule says who teaches each class',
    body: 'On your dancer\'s own sign-in, each class on My schedule, in the week list and on a day opened on the month, now says who teaches it and who the Jr Staff are. Each dance on My profile opens its class page with one tap, and their photo, or their first letter, has a ring in their own color.',
    link: { label: 'How it works', href: '/how-to?entry=dancer-calendar-dates' },
  },
  {
    // Bobbie asked for Picnic and Other in the event type picker. Worded from
    // the How-To entry add-important-date. Directors and Managers are the
    // roles that create and edit events (canManageCountdowns).
    id: 'event-types-picnic-other',
    date: '2026-10-26',
    roles: ['director', 'manager'],
    title: 'Picnic and Other are event types now',
    body: 'When you create or edit an event, the Type of date list has Picnic, and Other for a date that does not fit any of the named types.',
    link: { label: 'How to create an event', href: '/how-to?entry=add-important-date' },
  },
  {
    // Bobbie, 2026-09-16: "They also need to be able to read and view the
    // event pages. Not interact with them but view all of the info and view
    // those event pages". The change is on the dancer sign-in, which has no
    // What's new card of its own, so it goes to the grown-ups who set that
    // sign-in up. Every fact here is the How-To entry dancer-calendar-dates.
    id: 'dancer-event-pages-read-only',
    date: '2026-10-26',
    roles: ['family'],
    title: 'Your dancer can read the whole event page',
    body: 'On your dancer\'s own sign-in, opening an event under Events now shows its whole page: the schedule, hotel and travel details and photos, their call time, and on a competition, when they are on the comp team, their lineup and the updates posted for it. It is read only, so RSVPs stay with you.',
    link: { label: 'How it works', href: '/how-to?entry=dancer-calendar-dates' },
  },
  {
    // Bobbie, 2026-10-07: the Shout-out Wall goes into the card as its own
    // entry for every staff role, worded as "a reminder that it exsists if
    // they see someone being a leader or kind", "or a class that rocked it".
    // It never says or implies the reader has not posted. The roles are
    // everyone who can post one (SHOUTOUT_POSTER_ROLES in
    // netlify/functions/lib/shoutoutPublish.mjs), Admin included. "Straight
    // onto the wall" is the How-To entry shoutout-give: no approval step.
    id: SHOUTOUT_WALL_ENTRY_ID,
    date: '2026-10-26',
    roles: ['director', 'manager', 'company', 'ensemble', 'foh'],
    title: 'The Shout-out Wall is here for you',
    body: 'See a dancer being a leader or being kind, or a class that rocked it? Give them a shout-out. It goes straight onto the Shout-out Wall, for one dancer or a whole class.',
    link: { label: 'Open the Shout-out Wall', href: '/shoutout-wall' },
    featured: true,
  },
  {
    // Packet J18, worded from the How-To entry jr-staff-something-missing.
    // Jr Staff copy: nothing about how she signs in.
    id: 'jr-staff-something-missing',
    date: '2026-10-26',
    roles: ['ensemble'],
    title: 'Something missing on your home?',
    body: 'Tap Something missing? on your home and tell Ms. Kim what you expected to see. She gets what your account has and what your screen shows, so she can fix it.',
    link: { label: 'How it works', href: '/how-to?entry=jr-staff-something-missing' },
  },
  {
    // The same feature from the Director's side, worded from the line added
    // to the How-To entry link-jr-staff-own-sign-in.
    id: 'jr-staff-something-missing-report',
    date: '2026-10-26',
    roles: ['director'],
    title: 'Jr Staff can say when something is missing',
    body: 'A Jr Staff can tap Something missing? on her home. Kim gets one message in their conversation with what her account has, read by the Hub, and what her screen showed.',
    link: { label: 'What the report says', href: '/how-to?entry=link-jr-staff-own-sign-in' },
  },
];

/** A studio day (America/Chicago) as YYYY-MM-DD. */
export function studioDateKey(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** YYYY-MM-DD minus whole days, on the calendar (no clock involved). */
export function dateKeyMinusDays(key: string, days: number): string {
  const [year, month, day] = key.split('-').map(Number);
  const at = new Date(Date.UTC(year, month - 1, day));
  at.setUTCDate(at.getUTCDate() - days);
  return at.toISOString().slice(0, 10);
}

export interface WhatsNewSeenState {
  /** The studio day this person starts from: entries before it never show. */
  startDate: string | null;
  /** Ids already cleared with Got it. */
  seenIds: ReadonlySet<string>;
  /**
   * The studio day Got it was last tapped (on any sign-in), or null. A tap
   * in the current studio month puts the card away until next month.
   */
  clearedOn?: string | null;
}

/** The YYYY-MM before a YYYY-MM. */
export function previousMonthKey(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  return mon === 1 ? `${year - 1}-12` : `${year}-${String(mon - 1).padStart(2, '0')}`;
}

/** The month the card rolls up today: last studio month, as YYYY-MM. */
export function whatsNewRollupMonth(now: Date): string {
  return previousMonthKey(studioDateKey(now).slice(0, 7));
}

/** "October" (with the year when it is not this year's) for a YYYY-MM. */
export function whatsNewMonthLabel(month: string, now: Date): string {
  const [year, mon] = month.split('-').map(Number);
  const name = new Date(Date.UTC(year, mon - 1, 15)).toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  return year === Number(studioDateKey(now).slice(0, 4)) ? name : `${name} ${year}`;
}

/**
 * The entries this person should see right now, the featured one first, then
 * newest first.
 *
 * ONCE A MONTH, NOT DAILY (Bobbie, 2026-11-03: "only show what is new on the
 * hub once a month not daily"). The card is a monthly roll-up: it lists only
 * the changes dated in LAST studio month, so a change made today waits for
 * next month's card, and a Got it tapped this month puts the card away until
 * the 1st. Before this, every new entry brought the card back the next day.
 *
 * Also: nothing for a role it is not for, nothing cleared, nothing before her
 * start day, nothing past the age limit.
 */
export function visibleWhatsNew(
  role: UserRole | null | undefined,
  state: WhatsNewSeenState,
  now: Date,
  entries: readonly WhatsNewEntry[] = WHATS_NEW_ENTRIES,
): WhatsNewEntry[] {
  if (!role) return [];
  const today = studioDateKey(now);
  if (state.clearedOn && state.clearedOn.slice(0, 7) === today.slice(0, 7)) return [];
  const rollup = previousMonthKey(today.slice(0, 7));
  const oldest = dateKeyMinusDays(today, WHATS_NEW_MAX_AGE_DAYS);
  const floor = state.startDate && state.startDate > oldest ? state.startDate : oldest;
  return entries
    .filter((entry) => entry.roles.includes(role)
      && entry.date.slice(0, 7) === rollup
      && entry.date >= floor
      && !state.seenIds.has(entry.id))
    .sort((a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured))
      || b.date.localeCompare(a.date));
}

/**
 * How many entries this role could be shown today (last month's roll-up)
 * before any start day or clear. Zero means the card has nothing to say, so it asks the server for
 * nothing (no start write for a role with no news).
 */
export function whatsNewCandidates(
  role: UserRole | null | undefined,
  now: Date,
  entries: readonly WhatsNewEntry[] = WHATS_NEW_ENTRIES,
): number {
  return visibleWhatsNew(role, { startDate: null, seenIds: new Set() }, now, entries).length;
}

/** Plain-words search over title and body. */
export function searchWhatsNew(entries: readonly WhatsNewEntry[], query: string): WhatsNewEntry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...entries];
  return entries.filter((entry) => {
    const hay = `${entry.title} ${entry.body}`.toLowerCase();
    return words.every((word) => hay.includes(word));
  });
}

/**
 * The seen state, stored as one read cursor in users/{uid}/messageInboxState
 * (see netlify/functions/lib/whatsNewSeen.mjs for why there). The ids are a
 * comma list inside readThroughContent. The doc id is what
 * messageInboxStateId('bulletin', 'hub-whats-new') makes.
 */
export const WHATS_NEW_STATE_KIND = 'bulletin';
export const WHATS_NEW_STATE_ITEM_ID = 'hub-whats-new';
export const WHATS_NEW_STATE_DOC_ID = `${WHATS_NEW_STATE_KIND}__${WHATS_NEW_STATE_ITEM_ID}`;

export function parseSeenIds(content: unknown): Set<string> {
  if (typeof content !== 'string' || !content) return new Set();
  return new Set(content.split(',').map((id) => id.trim()).filter(Boolean));
}

/** The most ids one Got it request may carry; the card sends more in pieces. */
export const WHATS_NEW_CLEAR_MAX_IDS = 100;

/** Got it's ids in the pieces the lane accepts, in order, none empty. */
export function chunkWhatsNewIds(ids: readonly string[], size: number = WHATS_NEW_CLEAR_MAX_IDS): string[][] {
  const step = Math.max(1, Math.floor(size));
  const pieces: string[][] = [];
  for (let at = 0; at < ids.length; at += step) pieces.push(ids.slice(at, at + step));
  return pieces;
}
